import React from 'react';
import { PaperData } from '../types/paper';
import { Sparkles, Key, Plus, BookOpen, Layers, GitBranch, Swords, Zap } from 'lucide-react';

interface NavbarProps {
  currentPaper: PaperData;
  activeTab: 'wiki' | 'lineage' | 'flashcards' | 'battle';
  setActiveTab: (tab: 'wiki' | 'lineage' | 'flashcards' | 'battle') => void;
  onOpenIngestModal: () => void;
  onOpenKeyModal: () => void;
  onSelectInstantDemo: (topicId: 'transformer' | 'quantum' | 'crispr') => void;
}

export const Navbar: React.FC<NavbarProps> = ({
  currentPaper,
  activeTab,
  setActiveTab,
  onOpenIngestModal,
  onOpenKeyModal,
  onSelectInstantDemo
}) => {
  return (
    <header className="sticky top-0 z-40 bg-dark-bg/90 backdrop-blur-xl border-b border-slate-800/80 px-4 lg:px-8 py-3.5">
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center justify-between gap-4">
        
        {/* Brand Logo & Current Paper Title */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-tr from-brand-600 via-purple-600 to-cyan-400 p-0.5 shadow-glow-blue flex items-center justify-center shrink-0">
            <div className="w-full h-full bg-dark-bg rounded-[14px] flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-cyan-400" />
            </div>
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-extrabold text-lg tracking-tight text-white font-outfit">
                Scholar<span className="text-gradient">Wiki</span>
              </h1>
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-brand-900/60 text-cyan-300 border border-brand-500/30 font-mono">
                Gemini AI Studio
              </span>
            </div>
            <div className="text-xs text-slate-400 font-medium truncate max-w-xs sm:max-w-md">
              {currentPaper.title} ({currentPaper.year})
            </div>
          </div>
        </div>

        {/* View Mode Navigation Tabs */}
        <div className="flex items-center p-1 bg-dark-card/90 rounded-2xl border border-slate-800 shadow-inner overflow-x-auto">
          <button
            onClick={() => setActiveTab('wiki')}
            className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap ${
              activeTab === 'wiki'
                ? 'bg-gradient-to-r from-brand-600 to-cyan-600 text-white shadow-glow-blue'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <BookOpen className="w-4 h-4" /> Concept Wiki
          </button>
          <button
            onClick={() => setActiveTab('lineage')}
            className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap ${
              activeTab === 'lineage'
                ? 'bg-gradient-to-r from-brand-600 to-cyan-600 text-white shadow-glow-blue'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <GitBranch className="w-4 h-4" /> Reference Lineage
          </button>
          <button
            onClick={() => setActiveTab('flashcards')}
            className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap ${
              activeTab === 'flashcards'
                ? 'bg-gradient-to-r from-brand-600 to-cyan-600 text-white shadow-glow-blue'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <Layers className="w-4 h-4" /> Flashcards
          </button>
          <button
            onClick={() => setActiveTab('battle')}
            className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap ${
              activeTab === 'battle'
                ? 'bg-gradient-to-r from-rose-600 to-amber-600 text-white shadow-glow-rose'
                : 'text-rose-400 hover:text-rose-300'
            }`}
          >
            <Swords className="w-4 h-4" /> Monster Battle ⚔️
          </button>
        </div>

        {/* Instant Demo Quick-Select Buttons */}
        <div className="flex items-center gap-2">
          <div className="hidden lg:flex items-center gap-1.5 p-1 bg-dark-card/60 rounded-xl border border-slate-800 text-[11px]">
            <span className="text-slate-400 px-2 font-semibold flex items-center gap-1">
              <Zap className="w-3 h-3 text-amber-400" /> Instant Demos:
            </span>
            <button
              onClick={() => onSelectInstantDemo('transformer')}
              className={`px-2.5 py-1 rounded-lg font-semibold transition-all ${
                currentPaper.id === 'arxiv-1706-03762'
                  ? 'bg-brand-600 text-white shadow-glow-blue'
                  : 'bg-slate-800 text-slate-300 hover:text-white'
              }`}
            >
              ⚡ Transformer
            </button>
            <button
              onClick={() => onSelectInstantDemo('quantum')}
              className={`px-2.5 py-1 rounded-lg font-semibold transition-all ${
                currentPaper.id === 'topic-quantum-qkd-shor'
                  ? 'bg-purple-600 text-white shadow-glow-purple'
                  : 'bg-slate-800 text-slate-300 hover:text-white'
              }`}
            >
              ⚛️ Quantum QKD
            </button>
            <button
              onClick={() => onSelectInstantDemo('crispr')}
              className={`px-2.5 py-1 rounded-lg font-semibold transition-all ${
                currentPaper.id === 'topic-crispr-gene-editing'
                  ? 'bg-emerald-600 text-white shadow-glow-emerald'
                  : 'bg-slate-800 text-slate-300 hover:text-white'
              }`}
            >
              🧬 CRISPR-Cas9
            </button>
          </div>

          <button
            onClick={onOpenKeyModal}
            className="p-2 bg-dark-card hover:bg-slate-800 text-slate-300 border border-slate-700 rounded-xl text-xs font-semibold transition-all"
            title="Configure Gemini API Key"
          >
            <Key className="w-4 h-4 text-amber-400" />
          </button>
          <button
            onClick={onOpenIngestModal}
            className="flex items-center gap-1.5 px-4 py-2 bg-brand-600 hover:bg-brand-500 text-white rounded-xl text-xs font-bold shadow-glow-blue transition-all"
          >
            <Plus className="w-4 h-4" /> Add Topic / Paper
          </button>
        </div>

      </div>
    </header>
  );
};
